import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { cooperplaceApi } from 'services/api';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import DataTable from 'v3/components/DataTable';
import Button from 'v3/components/Button';
import moment from 'moment';

function formatDate(value) {
	if (!value) return 'Não informado';

	return moment(value).format('DD/MM/YYYY HH:mm:ss');
}

export default function ProfilesAuditModal({ isOpen, onClose }) {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedAudit, setSelectedAudit] = useState(null);

	async function fetchAudits({ page, perPageSize } = {}) {
		try {
			setIsLoading(true);
			const resolvedPage = page ?? currentPage;
			const resolvedPerPage = perPageSize ?? perPage;
			const response = await cooperplaceApi.get('profiles/audit', {
				params: {
					page: resolvedPage,
					perPage: resolvedPerPage,
				},
			});

			const payload = response.data || {};
			const rows = payload.data || [];
			const meta = payload.meta || {};
			setData(rows);
			setTotalItems(meta.total || rows.length);
			setTotalPages(meta.last_page || meta.lastPage || 1);
			if (meta.current_page && meta.current_page !== currentPage) {
				setCurrentPage(meta.current_page);
			}
			if (meta.per_page && meta.per_page !== perPage) {
				setPerPage(meta.per_page);
			}
		} catch (error) {
			setData([]);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		if (isOpen) {
			fetchAudits();
		}
	}, [isOpen, currentPage, perPage]);

	const columns = useMemo(
		() => [
			{
				name: 'Perfil',
				className: 'align-middle',
				selector: row => row.profile?.name || 'Não informado',
			},
			{
				name: 'Evento',
				className: 'align-middle',
				selector: row => row.eventType?.name || 'Não informado',
			},
			{
				name: 'Título',
				className: 'align-middle',
				selector: row => row.title || 'Não informado',
			},
			{
				name: 'Alterado por',
				className: 'align-middle',
				selector: row => row.userChange?.username || 'Sistema',
			},
			{
				name: 'Data',
				className: 'align-middle',
				selector: row => formatDate(row.createdAt),
			},
			{
				name: '',
				title: 'Detalhes',
				className: 'align-middle',
				render: row => (
					<Button
						variant="secondary"
						size="xs"
						onClick={() => {
							setSelectedAudit(row);
							setDetailsOpen(true);
						}}
					>
						Detalhes
					</Button>
				),
			},
		],
		[]
	);

	function handleClose() {
		setCurrentPage(1);
		setPerPage(10);
		onClose();
	}

	function handleDetailsClose() {
		setDetailsOpen(false);
		setSelectedAudit(null);
	}

	return (
		<>
			<Modal
				show={isOpen}
				onHide={handleClose}
				heading={
					<div className="d-flex align-items-center justify-content-between w-100">
						<Text color="dark" type="header">
							Auditoria de perfis
						</Text>
					</div>
				}
				footer={null}
				body={
					<Row>
						<Col xs={12}>
							<DataTable
								data={data}
								withFilterOption={false}
								columns={columns}
								total={totalItems}
								pages={totalPages}
								currentPage={currentPage}
								defaultPerPage={perPage}
								onPageChanged={page => setCurrentPage(page)}
								onPerPageChanged={value => {
									setPerPage(value);
									setCurrentPage(1);
								}}
								loading={isLoading}
								withPagination
							/>

							{data.length === 0 && !isLoading ? (
								<div className="text-center py-4">
									<Text type="regular" color="gray" as="p" className="mt-2">
										Nenhuma auditoria encontrada.
									</Text>
								</div>
							) : null}
						</Col>
					</Row>
				}
			/>
			<Modal
				show={detailsOpen}
				onHide={handleDetailsClose}
				heading={
					<div className="d-flex align-items-center justify-content-between w-100">
						<Text color="dark" type="header">
							Detalhes da auditoria
						</Text>
					</div>
				}
				footer={null}
				body={
					<Row>
						<Col xs={12}>
							<Text color="dark" type="regular" weight="500">
								{selectedAudit?.title || 'Detalhes'}
							</Text>
							<Row className="mt-3">
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Perfil
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.profile?.name || 'Não informado'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Evento
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.eventType?.name || 'Não informado'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Alterado por
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.userChange?.username || 'Sistema'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Data
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{formatDate(selectedAudit?.createdAt)}
									</Text>
								</Col>
							</Row>
							<Row>
								<Col xs={12}>
									<Text type="label" color="dark">
										Descrição
									</Text>
									<div className="mt-3">
										{(selectedAudit?.descriptions || '')
											.split('\n')
											.filter(Boolean)
											.map((line, index) => (
												<Text key={`${line}-${index}`} type="regular" color="gray" as="p" className="m-0 mt-1">
													{line}
												</Text>
											))}
										{!selectedAudit?.descriptions ? (
											<Text type="regular" color="gray" as="p" className="mt-2">
												Nenhuma informação detalhada disponível.
											</Text>
										) : null}
									</div>
								</Col>
							</Row>
						</Col>
					</Row>
				}
			/>
		</>
	);
}
